/**
 * «Слить записи в одну» — слияние дублей справочника культур (AK-569).
 *
 * Founder 10.08: «нет конструкции схлопнуть». Паттерн взят 1:1 из
 * `OpTypeMergeSheet` (AK-564), потому что стори у двух справочников одна и та
 * же и разъезжаться им нельзя:
 *
 *  · «Остаётся» вместо «эталон»: эталон в справочнике культур — это СИСТЕМНАЯ
 *    культура, звать тем же словом выжившую запись значит путать два предмета;
 *  · счётчики считаются сразу при выборе выжившей — без кнопки «посмотреть
 *    план»: лишний клик перед необратимым действием ничего не защищает;
 *  · то, что будет удалено/архивировано по конфликту, показано списком с явной
 *    галкой (решение founder 4 от 10.08);
 *  · необратимость — отдельной плашкой перед кнопкой.
 *
 * Своё против операций: разная привязка «в системе это» у культур не просто
 * предупреждение, а ГАРД — сервер отказывает, пока человек не подтвердил.
 * Две записи на разные каноны почти всегда две разные культуры, и молчаливое
 * слияние увело бы посевы не туда.
 */
import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { toast } from '@/lib/toast';
import { plural } from './catalog-review/reviewLabels';
import {
  cropVisibilityLabel,
  scopesLabel,
  sowingsCountLabel,
  useMergeCropRecords,
  type CropMergeResult,
  type CropQueueItem,
} from '@/lib/reference/tenantCrops';

/**
 * Ключи счётчиков сервера — это `таблица.колонка`. Человеку показываем
 * существительные; неизвестный ключ (появится новая точка переноса) НЕ печатаем
 * латиницей — лучше промолчать, чем показать `catalog_review_items`.
 */
const SITE_LABEL: Record<string, string> = {
  'sowings.local_catalog_item_id': 'посевов',
  'company_planned_prices.catalog_item_id': 'плановых цен закупки',
  'company_sale_prices.product_class_item_id': 'цен реализации',
  'catalog_review_items.source_item_id': 'строк разбора',
  'catalog_review_items.existing_item_id': 'строк разбора',
  'catalog_review_items.resolved_item_id': 'строк разбора',
};

/** Что именно значит «конфликт» в этой таблице — своими словами. */
const CONFLICT_LABEL: Record<string, string> = {
  'company_planned_prices.catalog_item_id':
    'плановые цены совпали по сезону — останется цена той записи, которая остаётся',
  'company_sale_prices.product_class_item_id':
    'цены реализации совпали по сезону и точке — останется цена той записи, которая остаётся',
};

/**
 * Свести счётчики всех источников по ЧЕЛОВЕЧЕСКОМУ имени, а не по ключу:
 * следы разбора приезжают тремя разными колонками одной таблицы, и человеку
 * незачем видеть «строк разбора» три раза подряд.
 */
function totalsByLabel(
  result: CropMergeResult | null,
  pick: 'repointed' | 'deleted_conflicts',
): Array<{ label: string; n: number; explain?: string }> {
  if (!result) return [];
  const acc = new Map<string, { label: string; n: number; explain?: string }>();
  for (const s of result.per_source) {
    for (const [key, n] of Object.entries(s[pick] ?? {})) {
      const label = SITE_LABEL[key];
      if (!n || !label) continue;
      const prev = acc.get(label);
      acc.set(label, {
        label,
        n: (prev?.n ?? 0) + n,
        explain: prev?.explain ?? CONFLICT_LABEL[key],
      });
    }
  }
  return [...acc.values()];
}

interface Props {
  /** Выбранные строки (2+). При меньшем числе панель не открывается. */
  selected: CropQueueItem[];
  open: boolean;
  onClose: () => void;
  onMerged?: (result: CropMergeResult) => void;
}

export function CropMergeSheet({ selected, open, onClose, onMerged }: Props) {
  const [targetId, setTargetId] = useState<string | null>(null);
  const [preview, setPreview] = useState<CropMergeResult | null>(null);
  const [conflictsAccepted, setConflictsAccepted] = useState(false);
  const [mixedAccepted, setMixedAccepted] = useState(false);
  const merge = useMergeCropRecords();

  /** Стабильный ключ набора: массив пересобирается на каждом рефетче списка. */
  const selectedKey = selected
    .map((s) => s.id)
    .sort()
    .join(',');

  /**
   * По умолчанию остаётся запись с наибольшим числом посевов — обычно она и
   * «правильная» (тот же выбор, что у тех.операций).
   */
  useEffect(() => {
    if (!open || selected.length === 0) return;
    const best = [...selected].sort((a, b) => (b.field_count ?? 0) - (a.field_count ?? 0))[0];
    setTargetId(best.id);
    setPreview(null);
    setConflictsAccepted(false);
    setMixedAccepted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedKey]);

  const target = useMemo(
    () => selected.find((s) => s.id === targetId) ?? null,
    [selected, targetId],
  );
  const sources = useMemo(
    () => selected.filter((s) => s.id !== targetId),
    [selected, targetId],
  );
  const sourceKey = sources
    .map((s) => s.id)
    .sort()
    .join(',');

  /** Записи с другой привязкой «в системе это» — гард сервера, не совет. */
  const mixedRefs =
    target !== null && sources.some((s) => (s.ref_id ?? null) !== (target.ref_id ?? null));
  const mixedBlocks = mixedRefs && !mixedAccepted;

  /**
   * Счётчики считаем сразу, как только известна пара «выжившая + источники».
   * `dry_run` на сервере откатывает транзакцию, побочных эффектов нет. Пока
   * разная привязка не подтверждена, предпросмотр НЕ запускаем: сервер вернул
   * бы 409, и человек увидел бы красную ошибку вместо вопроса, на который ему
   * и надо ответить.
   */
  useEffect(() => {
    if (!open || !target || sources.length === 0 || mixedBlocks) return;
    let cancelled = false;
    setPreview(null);
    setConflictsAccepted(false);
    merge
      .mutateAsync({
        target_id: target.id,
        source_ids: sources.map((s) => s.id),
        dry_run: true,
        allow_mixed_refs: mixedAccepted,
      })
      .then((result) => {
        if (!cancelled) setPreview(result);
      })
      .catch(() => {
        /* показывается через merge.isError ниже */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, targetId, sourceKey, mixedBlocks]);

  const repointed = totalsByLabel(preview, 'repointed');
  const conflicts = totalsByLabel(preview, 'deleted_conflicts');
  const hasConflicts = conflicts.length > 0;

  const busy = merge.isPending;
  const canMerge =
    !busy &&
    target !== null &&
    sources.length > 0 &&
    preview !== null &&
    !mixedBlocks &&
    (!hasConflicts || conflictsAccepted);

  /**
   * «Где будет видно» — ОТВЕТ СЕРВЕРА, а не догадка экрана.
   *
   * Раньше здесь считалось объединение областей по выбранным строкам, а
   * сервер область выжившей не расширял вовсе: на необратимом действии
   * человеку обещали то, чего не произойдёт. Теперь объединение делает
   * слияние, а предпросмотр показывает его результат; пока предпросмотр не
   * приехал — показываем область выжившей как есть.
   */
  const resultScope = useMemo(() => {
    if (preview?.result_scopes?.length) return scopesLabel(preview.result_scopes);
    return target ? cropVisibilityLabel(target) : '';
  }, [preview, target]);

  function confirm() {
    if (!target) return;
    merge.mutate(
      {
        target_id: target.id,
        source_ids: sources.map((s) => s.id),
        dry_run: false,
        allow_mixed_refs: mixedAccepted,
      },
      {
        onSuccess: (result) => {
          toast.success(`Записи слиты в «${target.local_name ?? '—'}»`);
          onMerged?.(result);
          onClose();
        },
        onError: (e) => toast.error(e.message || 'Не удалось слить записи'),
      },
    );
  }

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-4 overflow-y-auto sm:max-w-lg"
      >
        <SheetHeader>
          <SheetTitle>
            Слить {selected.length}{' '}
            {plural(selected.length, 'запись', 'записи', 'записей')} в одну
          </SheetTitle>
          <SheetDescription>
            Отметьте, какая запись останется. Все посевы и цены перепривяжутся на
            неё за одно действие, остальные записи уйдут в архив с пометкой, в
            какую именно их слили.
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label>Остаётся</Label>
            <RadioGroup
              className="flex flex-col gap-2"
              value={targetId ?? ''}
              onValueChange={(v) => setTargetId(v)}
            >
              {selected.map((row) => (
                <label
                  key={row.id}
                  htmlFor={`crop-survivor-${row.id}`}
                  className="flex items-center gap-3 rounded-md border border-border p-3 hover:bg-accent"
                >
                  <RadioGroupItem id={`crop-survivor-${row.id}`} value={row.id} />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm font-medium">
                      {row.local_name ?? '—'}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {row.canonical_name ?? 'своя запись'} · {cropVisibilityLabel(row)} ·{' '}
                      {sowingsCountLabel(row.field_count ?? 0)}
                    </span>
                  </div>
                </label>
              ))}
            </RadioGroup>
            <p className="text-xs text-muted-foreground">
              По умолчанию отмечена запись с наибольшим числом посевов — обычно
              она и «правильная».
            </p>
          </div>

          {/* Разная привязка — ГАРД: без подтверждения сервер откажет. */}
          {mixedRefs && (
            <Alert>
              <AlertTitle>Это разные культуры</AlertTitle>
              <AlertDescription className="flex flex-col gap-2">
                <span>
                  У выбранных записей разная привязка «в системе это». Обычно так
                  выглядят не дубли, а две разные культуры — посевы уехали бы не
                  туда. Проверьте прежде, чем сливать.
                </span>
                <label className="flex items-start gap-2 text-sm">
                  <Checkbox
                    checked={mixedAccepted}
                    onCheckedChange={(v) => setMixedAccepted(v === true)}
                    aria-label="Подтверждаю: это одна и та же культура"
                  />
                  <span>Проверил: это одна и та же культура, сливаю осознанно.</span>
                </label>
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-col gap-2 rounded-lg border border-border p-4">
            <p className="text-sm font-medium text-foreground">Что произойдёт</p>
            {mixedBlocks ? (
              <p className="text-sm text-muted-foreground">
                Сначала подтвердите, что это одна культура — тогда посчитаем, что
                и куда переедет.
              </p>
            ) : busy && !preview ? (
              <p className="text-sm text-muted-foreground">Считаем…</p>
            ) : (
              <>
                <ul className="flex flex-col gap-1 text-sm text-muted-foreground">
                  {repointed.length === 0 ? (
                    <li>Переносить нечего — по схлопываемым записям пусто.</li>
                  ) : (
                    repointed.map((r) => (
                      <li key={r.label}>
                        Перепривяжется {r.label}: {r.n}
                      </li>
                    ))
                  )}
                  <li>Уйдёт в архив записей: {preview?.archived.length ?? sources.length}</li>
                </ul>
                <p className="text-sm text-muted-foreground">
                  Где будет видно: {resultScope}
                </p>
              </>
            )}
          </div>

          {/* Явное подтверждение — решение founder 4 (10.08). */}
          {hasConflicts && (
            <Alert variant="destructive">
              <AlertTitle>Часть записей будет закрыта</AlertTitle>
              <AlertDescription className="flex flex-col gap-2">
                <ul className="flex list-disc flex-col gap-1 pl-4">
                  {conflicts.map((c) => (
                    <li key={c.label}>
                      {c.n} {c.label}:{' '}
                      {c.explain ?? 'останется запись той культуры, которая остаётся'}
                    </li>
                  ))}
                </ul>
                <label className="flex items-start gap-2 text-sm">
                  <Checkbox
                    checked={conflictsAccepted}
                    onCheckedChange={(v) => setConflictsAccepted(v === true)}
                    aria-label="Согласен, что перечисленные записи будут закрыты"
                  />
                  <span>Понимаю: перечисленное перестанет действовать.</span>
                </label>
              </AlertDescription>
            </Alert>
          )}

          <Alert variant="warning">
            <AlertTitle>Отменить слияние нельзя</AlertTitle>
            <AlertDescription>
              Схлопнутые записи останутся в архиве с пометкой, куда их слили, но
              посевы уже будут перепривязаны на ту, которая остаётся.
            </AlertDescription>
          </Alert>

          {merge.isError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{merge.error.message}</AlertDescription>
            </Alert>
          )}
        </div>

        <SheetFooter className="mt-auto flex-col gap-2 sm:flex-row">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Отмена
          </Button>
          <Button onClick={confirm} disabled={!canMerge}>
            {busy && merge.variables?.dry_run === false && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Слить
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

export default CropMergeSheet;
